import type { Habit } from '../services/habitService'
import { describeRRule, parseRRule, formatTime12h } from './rrule'
import { zoneAbbrev } from './taskForm'
import { formatDuration } from './dayShape'

/**
 * Words for a habit's schedule and cadence (Habits UX pass 2026-09-06). One
 * place, so the list row, the detail summary line and the create form say
 * the same thing. Every clock time names its zone — the definition's own
 * when the server sent one, else the browser's.
 */
const DAY_SHORT: Record<string, string> = {
  monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun',
}
const CADENCE_WORDS: Record<string, string> = { daily: 'Daily', weekly: 'Weekly', biweekly: 'Every two weeks', monthly: 'Monthly' }

/** "Weekly on Mon, Wed, Fri at 7:00 AM PDT" · "Daily at 6:45 AM PDT" · "Mon, Fri at 7:00 PM PDT" · "Daily" · null. */
export function scheduleWords(h: Pick<Habit, 'rrule' | 'scheduled_times_local' | 'preferred_days' | 'preferred_start_time' | 'target_cadence' | 'timezone'>): string | null {
  const zone = zoneAbbrev(h.timezone || null)
  const times = (h.scheduled_times_local || []).filter(Boolean)
  if (h.rrule && String(h.rrule).trim()) {
    const words = describeRRule(parseRRule(h.rrule), times)
      .replace(/^Weekly on Mon, Tue, Wed, Thu, Fri\b/, 'Weekdays')
      .replace(/^Weekly on Sat, Sun\b/, 'Weekends')
    return times.length ? `${words} ${zone}` : words
  }
  const days = (h.preferred_days || []).map((d) => DAY_SHORT[String(d).toLowerCase()] || d)
  const clock = h.preferred_start_time ? `${formatTime12h(h.preferred_start_time)} ${zone}` : times.length ? `${times.map(formatTime12h).join(', ')} ${zone}` : null
  if (days.length && clock) return `${days.join(', ')} at ${clock}`
  if (days.length) return days.join(', ')
  if (clock) return `${CADENCE_WORDS[String(h.target_cadence || '')] || 'Daily'} at ${clock}`
  if (h.target_cadence) return CADENCE_WORDS[String(h.target_cadence)] || String(h.target_cadence)
  return null
}

/** "45 min each time", "1 hr 30 min each time" — the one duration contract
 *  every surface keeps (`formatDuration`, North Star rule 4); this said
 *  "3h each time" until 2026-09-12. Null when unknown. */
export function durationWords(minutes?: number | null): string | null {
  const words = formatDuration(minutes)
  return words ? `${words} each time` : null
}

/** The run, said gently: "3 in a row" · "Fresh start" (after a slip) · null when nothing yet. */
export function runWords(streak: number | null | undefined, lastOutcome: string | null | undefined): string | null {
  if (streak && streak >= 2) return `${streak} in a row`
  if (streak === 1) return '1 so far'
  if (lastOutcome === 'missed') return 'Fresh start'
  return null
}

/** Follow-through reminders in words: "Reminds up to 4 times in the hour before, steadily" · "No reminders". */
export function reminderWords(h: Pick<Habit, 'nag_enabled' | 'nag_window_minutes' | 'nag_interval_minutes' | 'nag_max_per_window' | 'nag_style' | 'follow_through_mode'>): string {
  if (h.nag_enabled === false) return 'No reminders'
  const window = h.nag_window_minutes || 60
  const span = window % 60 === 0 ? `${window / 60 === 1 ? 'the hour' : `${window / 60} hours`}` : `${window} minutes`
  const style: Record<string, string> = { gentle: 'gently', steady: 'steadily', persistent: 'persistently', strict: 'firmly' }
  const mode: Record<string, string> = { hard_window: 'before', soft_window: 'around', all_day: 'through the day' }
  const when = h.follow_through_mode === 'all_day' ? 'through the day' : `in ${span} ${mode[String(h.follow_through_mode || 'hard_window')] || 'before'}`
  return `Reminds up to ${h.nag_max_per_window || 4} times ${when}, ${style[String(h.nag_style || 'persistent')] || 'steadily'}`
}
